<template>
  <div>
    <table-pagination class="mb-1" :pagination="pagination" :counter-label="$t('backup.tabs.monetary')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <b-table responsive class="bk-table" :items="rows" :fields="fields"
             no-local-sorting :sort-by="sort.field" :sort-desc="sortIsDesc"
             :busy="loading" hover show-empty :tbody-tr-class="rowClass"
             @sort-changed="onSortChanged" @row-clicked="onRowClick">
      <template #table-busy>
        <div class="text-center text-primary my-2"><b-spinner small class="mr-50"/>{{ $t('backup.loading') }}</div>
      </template>
      <template #empty>
        <backup-table-state :error="loadError ? errorText() : null" icon="fa-solid fa-coins"
                            :title="$t('backup.emptyMonetary')" :hint="$t('backup.emptyHint')" @retry="refresh"/>
      </template>

      <template #head(select)>
        <b-form-checkbox :checked="allPagePicked" :indeterminate="somePagePicked"
                         :disabled="!selectableRows.length" @change="togglePagePick"/>
      </template>
      <template #cell(select)="{ item }">
        <span v-b-tooltip.hover.right="selectBlockReason(item)">
          <b-form-checkbox class="no-click" :checked="isPicked(item)" :disabled="!isSelectable(item)"
                           @change="v => togglePick(item, v)"/>
        </span>
      </template>

      <template #cell(registrationTime)="{ value, item }">
        <div class="bk-two">
          <span>{{ bkDateTime(value) }}</span>
          <small class="bk-id">#{{ item.backupId }}</small>
        </div>
      </template>
      <template #cell(clientName)="{ item }">
        <backup-client-ref :name="item.clientName" :client-backup-id="item.clientBackupId"
                           :current-client-id="item.currentClientId"
                           @open-workspace="id => $emit('open-client', id)"/>
      </template>
      <template #cell(type)="{ item }">
        <div class="bk-two">
          <span class="font-weight-bold">{{ bkTypeLabel(item.type) }}</span>
          <small>{{ bkActionLabel(item.action) }}</small>
        </div>
      </template>
      <template #cell(amount)="{ item }">
        <span class="bk-amt" :class="bkAmountClass(item)">{{ bkMoney(item.amount, item.currency) }}</span>
      </template>
      <template #cell(status)="{ value }">
        <span class="bk-mstatus" :class="`bk-mstatus--${(value || '').toLowerCase()}`">{{ value }}</span>
      </template>
      <template #cell(realType)="{ item }">
        <span class="bk-tag" :class="{ 'bk-tag--fake': item.realType === 'FAKE' }">{{ item.realType }}</span>
        <span v-if="item.isFtd" class="bk-tag bk-tag--ftd">{{ item.ftdType || 'FTD' }}</span>
        <span v-if="item.isStd" class="bk-tag bk-tag--std">STD</span>
      </template>
      <template #cell(accountId)="{ item }">
        <span v-if="item.accountId">
          {{ item.accountId }}
          <i v-if="item.currentAccountExists === false" class="fa-solid fa-circle-exclamation text-warning ml-25"
             v-b-tooltip.hover.top="$t('backup.accountMissingNow')"></i>
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(depositorUserId)="{ value }">
        <span v-if="value">{{ bkUserName(value) }}</span><span v-else class="text-muted">—</span>
      </template>
      <template #cell(matchStatus)="{ item }">
        <backup-status-badge :status="item.matchStatus" :reason="item.matchReason"
                             :current-id="item.currentId" :client-id="item.currentClientId"/>
      </template>
      <template #cell(balance)="{ item }">
        <backup-balance-switch v-if="isSelectable(item)" :value="!!balance[item.backupId]"
                               :allowed="item.canApplyBalance === true"
                               :blocked-reason="balanceBlockReason(item)"
                               @input="v => setBalance(item, v)"/>
      </template>
    </b-table>

    <table-pagination v-if="rows.length > 10" class="mt-1" :pagination="pagination"
                      :counter-label="$t('backup.tabs.monetary')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <backup-bulk-bar :count="pickedCount" :label="$tc('backup.bulk.monetarySelected', pickedCount, { n: pickedCount })"
                     :balance-count="balanceItems.length" :balance-totals="totals"
                     :record-only-hint="$t('backup.bulk.recordOnlyHint')"
                     @clear="clearAll" @restore="emitPlan"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBackupTable from '@/components/mixin/mixinBackupTable'
import { isRestorable, groupRestoreRequests, balanceTotals } from '@/components/mixin/mixinBackup'
import TablePagination from '@/components/widget/TablePagination.vue'
import BackupStatusBadge from '@/components/partial/backup/BackupStatusBadge.vue'
import BackupTableState from '@/components/partial/backup/BackupTableState.vue'
import BackupBulkBar from '@/components/partial/backup/BackupBulkBar.vue'
import BackupClientRef from '@/components/partial/backup/BackupClientRef.vue'
import BackupBalanceSwitch from '@/components/partial/backup/BackupBalanceSwitch.vue'

export default {
  name: 'BackupMonetaryTable',
  components: { TablePagination, BackupStatusBadge, BackupTableState, BackupBulkBar, BackupClientRef, BackupBalanceSwitch },
  mixins: [mixinBackupTable],
  data: () => ({
    defaultSortField: 'registrationTime',
    // backupId → true when "Apply to trading balance" is ON (default OFF)
    balance: {},
  }),
  computed: {
    fields() {
      return [
        { key: 'select', label: '', class: 'bk-col-select' },
        { key: 'registrationTime', label: this.$t('backup.columns.time'), sortable: true },
        { key: 'clientName', label: this.$t('backup.columns.client'), sortable: true, class: 'bk-col-left' },
        { key: 'type', label: this.$t('backup.columns.type'), sortable: true },
        { key: 'amount', label: this.$t('backup.columns.amount'), sortable: true, class: 'bk-col-right' },
        { key: 'status', label: this.$t('backup.columns.monetaryStatus'), sortable: true },
        { key: 'realType', label: this.$t('backup.columns.realType') },
        { key: 'accountId', label: this.$t('backup.columns.account') },
        { key: 'depositorUserId', label: this.$t('backup.columns.depositor') },
        { key: 'matchStatus', label: this.$t('backup.columns.match'), sortable: true },
        { key: 'balance', label: this.$t('backup.applyToBalance') },
      ]
    },
    balanceItems() {
      return this.pickedRows.filter(r => this.balance[r.backupId])
    },
    totals() {
      return balanceTotals(this.pickedRows.map(row => ({ row, applyToBalance: !!this.balance[row.backupId] })))
    },
  },
  watch: {
    picked(v) {
      // Balance switches only live on picked rows.
      const next = {}
      Object.keys(this.balance).forEach(id => { if (v[id] && this.balance[id]) next[id] = true })
      if (Object.keys(next).length !== Object.keys(this.balance).length) this.balance = next
    },
  },
  methods: {
    ...mapActions('backup', ['getAllBackupMonetary']),
    fetchRows(params) {
      return this.getAllBackupMonetary(params)
    },
    isSelectable(row) {
      return isRestorable(row.matchStatus) && row.currentClientId != null && row.currentClientId !== ''
    },
    selectBlockReason(row) {
      if (this.isSelectable(row)) return ''
      if (!isRestorable(row.matchStatus)) return this.$t('backup.notRestorable')
      return this.$t('backup.restoreClientFirst')
    },
    balanceBlockReason(row) {
      if (row.canApplyBalance === true) return ''
      if (row.status !== 'APPROVED') return this.$t('backup.balanceBlock.notApproved')
      if (row.action === 'TAXES') return this.$t('backup.balanceBlock.taxes')
      if (row.currentAccountExists === false) return this.$t('backup.balanceBlock.noAccount', { account: row.accountId || '' })
      return this.$t('backup.balanceBlock.generic')
    },
    setBalance(row, on) {
      if (on && row.canApplyBalance !== true) return
      const next = { ...this.balance }
      if (on) {
        next[row.backupId] = true
        if (!this.isPicked(row)) this.togglePick(row, true)
      } else {
        delete next[row.backupId]
      }
      this.balance = next
    },
    onRowClick(row, index, ev) {
      if (ev && ev.target && ev.target.closest && ev.target.closest('.no-click, .custom-control, a, button')) return
      if (this.isSelectable(row)) this.togglePick(row, !this.isPicked(row))
    },
    rowClass(item) {
      if (!item) return ''
      // status bar: orange = something to recover/review, green = 100% the same
      const same = item.matchStatus === 'EXISTS' || item.matchStatus === 'RESTORED'
      return ['bk-row', `bk-row--${(item.matchStatus || '').toLowerCase()}`, same ? 'bk-row--same' : 'bk-row--todo',
        this.isPicked(item) ? 'bk-row--picked' : '', this.isSelectable(item) ? 'bk-row--selectable' : '']
    },
    clearAll() {
      this.clearPicked()
      this.balance = {}
    },
    emitPlan() {
      const monetary = this.pickedRows.map(row => ({ row, applyToBalance: !!this.balance[row.backupId] }))
      this.$emit('restore-plan', {
        source: 'monetary',
        sections: { client: null, accounts: [], fields: [], monetary, trades: [], notes: [] },
        requests: groupRestoreRequests({ monetary }),
      })
    },
    afterRestore() {
      this.clearAll()
      this.refresh()
    },
  },
}
</script>
